import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { IntlShape, injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import api from '@/shared/api';
import intlShape from '@/shared/utils/intlShape';
import config from '@/config';

import FixedTree from '@/shared/components/FixedTree';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import { emptyGuid } from '@/shared/utils/guid';

import { getDragValue, containsDragFormat } from '@/shared/utils/dragEventHelpers';
import { TagIcon, FolderIcon } from '@/shared/components/Icons';
import userStorage from '@/shared/services/userStorage';
import appInsights from '@/appInsights';

const messages = defineMessages({
  AllDocuments: 'All documents',
  DocumentsWithoutTags: 'Documents without tags',
  Folders: 'Folders',
  Tags: 'Tags',
  DropDocumentToAddTag: 'Drop document to add tag',
  MoveOrCopyDocument: 'Move or copy document',
  UploadFilesToSpecificFolder: 'Upload files to specific folder',
});

const findInitialNode = (node, tagsToFind) => {
  if (tagsToFind.length === 1 && node.tagId && node.tagId === tagsToFind[0]) return node;
  if (tagsToFind.length === 1 && !node.tagId && node.children && node.children.length) {
    for (let i = 0; i < node.children.length; i += 1) {
      const found = findInitialNode(node.children[i], tagsToFind);
      if (found) {
        // eslint-disable-next-line no-param-reassign
        node.collapsed = false;
        return found;
      }
    }
  }
  if (tagsToFind.length > 1) {
    const newTagsToFind = node.tagId ? tagsToFind.filter(t => t !== node.tagId) : tagsToFind;
    if (node.tagId && tagsToFind.length === newTagsToFind) return null; // The current node doesn't have one of the tags
    if (!node.children || !node.children.length) return null;
    for (let i = 0; i < node.children.length; i += 1) {
      const found = findInitialNode(node.children[i], newTagsToFind);
      if (found) {
        // eslint-disable-next-line no-param-reassign
        node.collapsed = false;
        return found;
      }
    }
  }
  return null;
};
const findNodeById = (id, tree) => {
  if (tree.id === id) return tree;
  if (!tree.children || !tree.children.length) return null;
  for (let i = 0; i < tree.children.length; i += 1) {
    const found = findNodeById(id, tree.children[i]);
    if (found) {
      // eslint-disable-next-line no-param-reassign
      tree.collapsed = false;
      return found;
    }
  }
  return null;
};

const updateNode = (nodeToFind, currentNode, updatedProps) => {
  if (nodeToFind === currentNode) return { ...currentNode, ...updatedProps };
  if (!currentNode.children || !currentNode.children.length) return currentNode;
  return {
    ...currentNode,
    children: currentNode.children.map(child => updateNode(nodeToFind, child, updatedProps)),
  };
};

const getCollapsedState = () => {
  try {
    return userStorage.getItem('DocumentTreeCollapsed') || {};
  } catch (e) {
    return {};
  }
};

const saveCollapsedState = value => {
  try {
    userStorage.setItem('DocumentTreeCollapsed', value);
  } catch (e) {
    // ignore
  }
};

const getCollapsedStateFromTree = tree => {
  if (tree.leaf || !tree.children || !tree.children.length) return;
  return {
    [tree.id]: tree.collapsed,
    ...tree.children.reduce((o, c) => ({ ...o, ...getCollapsedStateFromTree(c) }), {}),
  };
};

const undefinedFallback = (value, fallback) => (typeof value === 'undefined' ? fallback : value);

class DocumentTreeView extends Component {
  state = {
    tagTree: null,
    tagList: null,
    showList: false,
    activeNode: null,
    dragOverNode: null,
  };
  mapTreeNode = ({ children = null, ...node }, parentTags = [], collapsedState = {}, lvl = 0) => {
    const tags = [...parentTags, node.tagId];

    return {
      ...node,
      collapsed: undefinedFallback(collapsedState[node.id], lvl > 0),
      tags,
      children:
        children && children.length
          ? children.map(c => this.mapTreeNode(c, tags, collapsedState, lvl + 1))
          : [],
    };
  };
  componentDidMount() {
    const { intl, initialTags, onTreeLoaded } = this.props;

    const collapsedState = getCollapsedState();

    Promise.all([
      api.base.get(`${config.appUrls.documents}/api/Tree`),
      api.base.get(`${config.appUrls.documents}/api/Tag`),
    ]).then(([{ data: treeData }, { data: tagData }]) => {
      this.allDocumentsNode = {
        id: 'AllDocuments',
        name: intl.formatMessage(messages.AllDocuments),
        collapsed: undefinedFallback(collapsedState.AllDocuments, false),
        noDrop: true,
        leaf: true,
      };
      const treeNodes = treeData.map(n => this.mapTreeNode(n, [], collapsedState, 1));
      const allTagsNode = {
        id: 'AllTags',
        name: intl.formatMessage(messages.AllDocuments),
        collapsed: undefinedFallback(collapsedState.AllTags, true),
        tags: null,
        noDrop: true,
        leaf: true,
      };
      const tagNodes = tagData.map(x => ({
        id: `t-${x.id}`,
        name: x.name,
        tagId: x.id,
        tags: [x.id],
        collapsed: false,
        leaf: true,
        alwaysCopy: true,
      }));
      const noTagsNode = {
        id: 'NoTags',
        name: intl.formatMessage(messages.DocumentsWithoutTags),
        collapsed: false,
        leaf: true,
        tags: [emptyGuid],
        noDrop: true,
      };
      const tagTree = {
        id: 'Root',
        name: '',
        children: [noTagsNode, this.allDocumentsNode, ...treeNodes],
        collapsed: false,
        leaf: true,
      };
      const tagList = {
        id: 'ListRoot',
        name: '',
        children: [noTagsNode, allTagsNode, ...tagNodes],
        collapsed: false,
        leaf: true,
      };
      let showList = false;
      let activeNode = null;
      const tags = initialTags ? initialTags.split(';') : [];
      if (tags && tags.length) {
        if (tags.length === 1) {
          showList = true;
          activeNode = tagNodes.find(x => x.tagId === tags[0]);
        } else {
          activeNode = findInitialNode(tagTree, tags);
        }
      }
      if (!activeNode) {
        const activeNodeId = userStorage.getItem('DocumentTreeActiveNode');
        if (activeNodeId) {
          if (activeNodeId.indexOf('t-') === 0) {
            activeNode = tagNodes.find(x => x.id === activeNodeId);
            showList = true;
          } else {
            activeNode = findNodeById(activeNodeId, tagTree);
          }
        }
      }
      activeNode = activeNode || this.allDocumentsNode;

      this.setState(
        {
          tagTree,
          tagList,
          activeNode,
          showList,
        },
        () => {
          onTreeLoaded({ tags: activeNode.tags });
        },
      );
    });
  }

  componentDidUpdate(prevProps) {
    const { initialTags } = this.props;
    if (prevProps.initialTags && !initialTags && this.allDocumentsNode) {
      userStorage.setItem('DocumentTreeActiveNode', null);
      this.setState({ activeNode: this.allDocumentsNode, showList: false });
    }
  }

  collapseNode = (node, collapsed) => {
    if (node.leaf || !node.id) return;
    const { tagTree } = this.state;
    const collapsedState = getCollapsedState();
    collapsedState[node.id] = collapsed;
    saveCollapsedState(collapsedState);
    return updateNode(node, tagTree, { collapsed });
  };

  selectNode = node => {
    const { onFilterChanged } = this.props;
    const { activeNode, showList } = this.state;
    const updatedState = { activeNode: node };
    userStorage.setItem('DocumentTreeActiveNode', node.id);
    if (!node.leaf) {
      if (node.id !== activeNode.id && node.collapsed) {
        updatedState.tagTree = this.collapseNode(node, false);
      } else if (node.id === activeNode.id) {
        updatedState.tagTree = this.collapseNode(node, !node.collapsed);
      }
    }
    if (showList && node) {
      appInsights().trackEvent({ name: 'DocumentsTagListClicked' }, { tag: node.name });
    }
    this.setState(updatedState, () => onFilterChanged({ tags: node.tags }));
  };

  handleDragOver = (e, node) => {
    if (!e.dataTransfer) return;
    const { activeNode } = this.state;

    try {
      if (containsDragFormat(e, 'Files')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        this.setState({ dragOverNode: node });
      } else if (containsDragFormat(e, 'cxdocument') || containsDragFormat(e, 'Text')) {
        if (activeNode.id === node.id || !node.tags || !node.tags.length || node.noDrop) return;
        e.preventDefault();
        e.dataTransfer.dropEffect =
          e.ctrlKey || !activeNode || !activeNode.tags || !activeNode.tags.length || node.alwaysCopy
            ? 'copy'
            : 'move';
        this.setState({ dragOverNode: node });
      }
    } catch (ex) {
      console.error(ex);
    }
  };
  handleDragLeave = (e, node) => {
    if (!e.dataTransfer) return;
    this.setState(state => ({
      dragOverNode: state.dragOverNode === node ? null : state.dragOverNode,
    }));
  };
  handleDrop = (e, node) => {
    if (!e.dataTransfer) return;
    const { activeNode } = this.state;
    const { onFilterChanged, onUploadDocument, allowUpload } = this.props;
    if (!allowUpload) return;
    this.setState({
      dragOverNode: null,
    });
    try {
      if (containsDragFormat(e, 'Files')) {
        e.preventDefault();
        onUploadDocument({ files: [...e.dataTransfer.files], node });
      } else {
        const dragValue = getDragValue(e, 'cxdocument') || getDragValue(e, 'Text');
        if (dragValue) {
          e.preventDefault();
          if (activeNode.id === node.id || !node.tags || !node.tags.length || node.noDrop) return;
          const data = JSON.parse(dragValue);
          if (data && data.id) {
            const model = {
              documentId: data.id,
              add: node.tags,
              remove:
                activeNode && activeNode.tags && !e.ctrlKey && !node.alwaysCopy
                  ? activeNode.tags
                  : [],
            };
            api.base.put(`${config.appUrls.documents}/api/Documents/Tags`, model).then(() => {
              if (model.remove.length) {
                onFilterChanged({ tags: activeNode.tags, force: true });
              }
            });
          }
        }
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  handleTreeChanged = tree => {
    const collapsedState = getCollapsedStateFromTree(tree);
    saveCollapsedState(collapsedState);
  };

  toggleState = showList => {
    const { tagTree, tagList } = this.state;
    const { onFilterChanged } = this.props;
    const tree = showList ? tagList : tagTree;
    const activeNode = tree.children[0];
    this.setState({ showList, activeNode }, () => onFilterChanged({ tags: activeNode.tags }));

    if (showList) appInsights().trackEvent({ name: 'DocumentsTagListClicked' });
  };

  renderNode = node => {
    const { activeNode, dragOverNode } = this.state;
    const { allowUpload } = this.props;
    return (
      <div
        className={classnames('node', {
          active: activeNode && node.id === activeNode.id,
          'drop-hover': allowUpload && dragOverNode && node.id === dragOverNode.id,
        })}
        id={node.id}
        onClick={() => this.selectNode(node)}
        onDragOver={e => this.handleDragOver(e, node)}
        onDragLeave={e => this.handleDragLeave(e, node)}
        onDrop={e => this.handleDrop(e, node)}
      >
        {node.name}
      </div>
    );
  };

  render() {
    const { tagTree, showList, tagList } = this.state;
    const { intl } = this.props;
    return (
      <div className="sticky">
        <div className="btn-group full-width margin5B" role="group">
          <a
            className={classnames('btn btn-default', { active: !showList })}
            onClick={() => this.toggleState(false)}
          >
            <FolderIcon />
            <FormattedMessage {...messages.Folders} />
          </a>
          <a
            className={classnames('btn btn-default', { active: showList })}
            onClick={() => this.toggleState(true)}
          >
            <TagIcon />
            <FormattedMessage {...messages.Tags} />
          </a>
        </div>
        <div
          className="document-tree document-tree-fixed hidden-root dropzone dropable-files dropable-cxdocument"
          dropinfo-cxdocument={intl.formatMessage(
            showList ? messages.DropDocumentToAddTag : messages.MoveOrCopyDocument,
          )}
          dropinfo-files={intl.formatMessage(messages.UploadFilesToSpecificFolder)}
        >
          {tagTree ? (
            <FixedTree
              paddingLeft={20}
              tree={showList ? tagList : tagTree}
              renderNode={this.renderNode}
              onChange={this.handleTreeChanged}
            />
          ) : (
            <LoadingAnimation />
          )}
        </div>
      </div>
    );
  }
}

DocumentTreeView.propTypes = {
  intl: intlShape.isRequired,
  onFilterChanged: PropTypes.func.isRequired,
  onUploadDocument: PropTypes.func,
  initialTags: PropTypes.string,
  onTreeLoaded: PropTypes.func,
  allowUpload: PropTypes.bool,
};
DocumentTreeView.defaultProps = {
  initialTags: null,
  allowUpload: true,
  onUploadDocument: () => {},
  onTreeLoaded: () => {},
};

export default injectIntl(DocumentTreeView);
